import {
    createChannel,
    CreateChannelPayload,
    deleteChannel,
    deleteJoinedChannel,
    enterQuitChannel,
    EnterQuitChannelResult,
    getJoinedChannels,
    getSelfCreatedChannels,
    ListChannelsType,
    queryChannelInfo,
    queryChannelNum,
    QueryChannelNumResult,
    QueryChannelResultData,
    updateChannel,
    UpdateChannelPayload,
} from "@obsbot/remo-server-api";
import { IServiceVideoChatUID } from "@obsbot/remo-services";
import { create } from "zustand";
import { persist } from "zustand/middleware";
import { LS_VERSION } from "./utils/LS-Store";
import { useUserStore } from "./user-store";
import { useChannelRoomStore } from "./channelroom-store";
import { usePreferencesStore as getPreferencesStoreHook } from "./preferences-store";
import { globalStore } from "./global-store";

export type ChannelItem = QueryChannelResultData;

export interface ChannelCurUserState {
    userUUID: IServiceVideoChatUID;
    camera: boolean;
    mic: boolean;
    isListen: boolean;
    controlMic?: boolean; // 耳机打开时， 是否单独控制麦克风
}

export interface ChannelStoreState {
    userState: ChannelCurUserState;
    /**
     * 频道列表
     *  - key: channelUUID
     *  - value: {@link ChannelItem}
     */
    channels: Map<string, ChannelItem>;
    /**
     * 频道在线人数
     */
    channelMembers: Map<string, string[]>;
    /**
     * 激活的频道
     */
    activeChannel: ChannelItem | null;
    /**
     * 是否拥有频道
     */
    hasChannels: boolean;
    /**
     * 获取频道列表
     * @param type - 频道类型 {@link ListChannelsType}
     * @param uid - 用户ID
     * @returns {Promise<ChannelItem[]>} 返回频道UUID列表
     */
    listChannels: (type: ListChannelsType, uid: string) => Promise<ChannelItem[]>;
    /**
     * 加入频道
     * @param channelUUID - 频道UUID
     * @returns {Promise<EnterQuitChannelResult>} 返回加入频道结果 {@link EnterQuitChannelResult}
     */
    joinChannel: (channelUUID: string) => Promise<EnterQuitChannelResult>;
    /**
     * 查询频道信息
     * @param channelUUID - 频道UUID
     * @returns {Promise<QueryChannelResultData>} 返回加入频道结果 {@link QueryChannelResultData}
     */
    queryChannelInfo: (channelUUID: string) => Promise<QueryChannelResultData>;
    /**
     * 查询频道成员数量
     * @param channelUUID - 频道UUID
     * @returns {Promise<QueryChannelNumResult>} 返回加入频道结果 {@link QueryChannelNumResult}
     */
    queryChannelNum: (channelUUID: string) => Promise<QueryChannelNumResult>;
    /**
     * 退出频道
     * @param channelUUID - 频道UUID
     * @returns {Promise<EnterQuitChannelResult>} 返回退出频道结果 {@link EnterQuitChannelResult}
     */
    quitChannel: (channelUUID: string) => Promise<EnterQuitChannelResult>;
    /**
     * 创建频道
     * @param payload - 创建频道的参数 {@link CreateChannelPayload}
     * @returns {Promise<string>} 返回创建频道的UUID
     */
    createChannel: (payload: CreateChannelPayload) => Promise<string>;
    /**
     * 删除频道
     * @param channelUUID - 频道UUID
     * @returns {Promise<void>}
     */
    deleteChannel: (channelUUID: string) => Promise<void>;
    /**
     * 删除已经加入的频道
     * @param channelUUID - 频道UUID
     * @returns {Promise<void>}
     */
    deleteJoinedChannel: (channelUUID: string) => Promise<void>;
    /**
     * 更新频道
     * @param payload - 更新频道的参数 {@link UpdateChannelPayload}
     * @returns {Promise<void>}
     */
    updateChannel: (payload: UpdateChannelPayload) => Promise<void>;
    /**
     * 更新当前的频道
     */
    updateActiveChannel: (channel: ChannelItem | null) => void;
    /**
     * 更新当前用户的state
     */
    updateUserState: (partialUser: Partial<ChannelCurUserState>) => void;
    /**
     * 更新频道的invite_flag
     */
    updateChannelInviteFlag: (channelUuid: string, inviteFlag: string) => void;
    /**
     * 销毁仓库
     */
    destroy: () => void;
}

export const useChannelStore = create<ChannelStoreState>()(
    persist(
        (set, get) => ({
            userState: {
                camera: false,
                mic: false,
                isListen: false,
                userUUID: "",
                controlMic: true,
            },
            channels: new Map(),
            channelMembers: new Map(),
            hasChannels: false,
            activeChannel: null,
            listChannels: async (type: ListChannelsType, userId: string) => {
                const fn =
                    type === ListChannelsType.CREATED ? getSelfCreatedChannels : getJoinedChannels;
                const channels = await fn(userId);
                const channelItems = channels.data;

                const channelMap = new Map<string, ChannelItem>(get().channels);
                for (const item of channelItems) {
                    channelMap.set(item.channelUuid, item);
                }
                if (channelItems.length && type === ListChannelsType.CREATED) {
                    set({ hasChannels: true });
                } else if (type === ListChannelsType.CREATED && !channelItems.length) {
                    set({ hasChannels: false });
                }
                set({ channels: channelMap });
                return channelItems;
            },
            joinChannel: async (channelUUID: string) => {
                if (channelUUID !== get().activeChannel?.channelUuid) {
                    const channels = get().channels;
                    const channelItem = channels.get(channelUUID);
                    if (channelItem) {
                        get().updateActiveChannel(channelItem);
                    }
                }
                return enterQuitChannel({ channelUuid: channelUUID, inChannelStatus: 1 });
            },
            queryChannelInfo: async (channelUUID: string) => {
                const channel = await queryChannelInfo(channelUUID);
                const newMap = new Map(get().channels);
                newMap.set(channelUUID, channel);
                set({ channels: newMap });
                return channel;
            },
            queryChannelNum: async (channelUUID: string) => {
                const result = await queryChannelNum(channelUUID);
                const newMap = new Map(get().channelMembers);
                newMap.set(channelUUID, result.data);
                set({ channelMembers: newMap });
                return result;
            },
            quitChannel: async (channelUUID: string) => {
                return enterQuitChannel({ channelUuid: channelUUID, inChannelStatus: 0 });
            },
            createChannel: async (payload: CreateChannelPayload) => {
                const preferencesStore = getPreferencesStoreHook(
                    globalStore.userInfo?.user_id ?? "",
                ).getState();
                if (
                    get().hasChannels === false &&
                    preferencesStore.hasShowedChannelGuide === true
                ) {
                    preferencesStore.updateShowChannelGuide(null);
                }
                const result = await createChannel(payload);
                return result.data;
            },
            updateChannel: async payload => {
                await updateChannel(payload);
                const messageInterval = payload.timeInterval ?? 0;
                const rtm = useChannelRoomStore.getState().rtm;
                if (rtm) {
                    (rtm as any).messageInterval = messageInterval;
                }
            },
            deleteChannel: async (channelUUID: string) => {
                await deleteChannel({ channelUuid: channelUUID });
                const newChannels = get().channels;
                newChannels.delete(channelUUID);
                set({ channels: newChannels });
                if (!newChannels.size) {
                    set({ activeChannel: null });
                }
            },
            deleteJoinedChannel: async channelUUID => {
                await deleteJoinedChannel({ channelUuid: channelUUID });
                const newChannels = get().channels;
                newChannels.delete(channelUUID);
                set({ channels: newChannels });
                if (!newChannels.size) {
                    set({ activeChannel: null });
                }
            },
            updateActiveChannel: channel => {
                set({ activeChannel: channel });
            },
            updateUserState(partialUser) {
                (window as any).electron?.ipcRenderer.send("resend-to-overlay-window", {
                    eventName: "update-current-user",
                    data: { ...useUserStore.getState().currentUser, ...partialUser },
                });
                set({ userState: { ...get().userState, ...partialUser } });
            },
            updateChannelInviteFlag(channelUuid, inviteFlag) {
                const allChannels = get().channels;
                const channel = allChannels.get(channelUuid);
                if (channel) {
                    allChannels.set(channelUuid, { ...channel, inviteFlag });
                    set({ channels: allChannels });
                }
            },
            destroy() {
                set({ activeChannel: null, channels: new Map() });
            },
        }),
        {
            name: "Remo-Channel-Store",
            version: LS_VERSION,
            partialize: state => ({
                hasChannels: state.hasChannels,
            }),
        },
    ),
);

export const channelStore = useChannelStore.getState();
